import React, { useState, useEffect } from "react";
import { ActionIcon, Button, Popover, Stack, Box, Text, Group, Checkbox } from "@mantine/core";
import { MdPets } from "react-icons/md";
import { useTranslation } from "react-i18next";
import { usePopoverManager } from "./PopoverManager";

interface DomainFilterControlProps {
    value: string[]; // Selected domains
    onChange: (domains: string[]) => void;
    availableDomains: string[]; // All available domains from data
    disabled?: boolean;
    loading?: boolean;
}

export const DomainFilterControl: React.FC<DomainFilterControlProps> = ({ value, onChange, availableDomains, disabled = false, loading = false }) => {
    const { t } = useTranslation();
    const { isPopoverOpen, togglePopover, setOpenPopover } = usePopoverManager();
    const [tempSelection, setTempSelection] = useState<string[]>(value);

    const popoverId = "domain-filter";
    const opened = isPopoverOpen(popoverId);

    // Update temp selection when value changes from parent
    useEffect(() => {
        setTempSelection(value);
    }, [value]);

    const handleToggle = () => {
        const wasOpened = opened;
        togglePopover(popoverId);

        if (!wasOpened) {
            // Reset temp selection to current value when opening
            setTempSelection(value);
        }
    };

    const handleDomainToggle = (domain: string) => {
        setTempSelection(prev => (prev.includes(domain) ? prev.filter(d => d !== domain) : [...prev, domain]));
    };

    const handleSelectAll = () => {
        setTempSelection([...availableDomains]);
    };

    const handleClearAll = () => {
        setTempSelection([]);
    };

    const handleApply = () => {
        onChange(tempSelection);
        setOpenPopover(null);
    };

    const handleCancel = () => {
        setTempSelection(value);
        setOpenPopover(null);
    };

    const handleClose = () => {
        // Only close if this popover is actually the one that's open
        if (opened) {
            handleCancel();
        }
    };

    // Check if there are pending changes
    const hasPendingChanges =
        tempSelection.length !== value.length ||
        !tempSelection.every(domain => value.includes(domain)) ||
        !value.every(domain => tempSelection.includes(domain));

    // Always show the filter as having no active filters when no domains are available
    // or when all available domains are selected (which means "show all")
    const hasFilters = availableDomains.length > 0 && value.length > 0 && value.length < availableDomains.length;

    const labelFor = (domain: string) => (domain === "human" || domain === "vet" ? t(`domain.${domain}`) : domain || "Unknown");

    const displayText = availableDomains.length === 0 || value.length === 0 || value.length === availableDomains.length ? "All Domains" : `${value.length}`;

    return (
        <Popover
            opened={opened}
            withArrow={false}
            closeOnClickOutside={false}
            closeOnEscape={true}
            trapFocus={false}
            withinPortal={true}
            position="bottom-start"
            offset={4}
            onClose={handleClose}
            styles={{
                dropdown: {
                    padding: "12px",
                    minWidth: "240px",
                    maxWidth: "320px",
                    border: "2px solid #339af0",
                    borderRadius: "8px",
                    boxShadow: "0 4px 12px rgba(51, 154, 240, 0.15)",
                    backgroundColor: "#ffffff"
                }
            }}
        >
            <Popover.Target>
                <ActionIcon
                    variant={hasFilters ? "filled" : "light"}
                    size="sm"
                    onClick={handleToggle}
                    disabled={disabled || loading}
                    color={hasFilters ? "blue" : undefined}
                    title={displayText}
                >
                    <MdPets size={16} />
                </ActionIcon>
            </Popover.Target>
            <Popover.Dropdown>
                <Stack gap="sm">
                    {availableDomains.length > 0 ? (
                        <>
                            {/* Select All / Clear All buttons */}
                            <Group gap="xs">
                                <Button size="xs" variant="subtle" onClick={handleSelectAll} disabled={tempSelection.length === availableDomains.length}>
                                    Select All
                                </Button>
                                <Button size="xs" variant="subtle" onClick={handleClearAll} disabled={tempSelection.length === 0}>
                                    Clear All
                                </Button>
                            </Group>

                            {/* Domain List */}
                            <Stack gap="xs">
                                {availableDomains.map(domain => (
                                    <Box key={domain}>
                                        <Checkbox
                                            label={labelFor(domain)}
                                            checked={tempSelection.includes(domain)}
                                            onChange={() => handleDomainToggle(domain)}
                                            size="sm"
                                            styles={{
                                                label: {
                                                    fontSize: "14px",
                                                    cursor: "pointer"
                                                },
                                                input: {
                                                    cursor: "pointer"
                                                }
                                            }}
                                        />
                                    </Box>
                                ))}
                            </Stack>

                            {/* Apply/Cancel buttons - always visible */}
                            <Group gap="xs" grow>
                                <Button size="xs" variant="filled" onClick={handleApply} color="blue" disabled={!hasPendingChanges}>
                                    Apply Filter
                                </Button>
                                <Button size="xs" variant="subtle" onClick={handleCancel}>
                                    Cancel
                                </Button>
                            </Group>
                        </>
                    ) : (
                        <Text size="sm" c="dimmed" ta="center" py="md">
                            No domain data available.
                            <br />
                            Showing all records.
                        </Text>
                    )}
                </Stack>
            </Popover.Dropdown>
        </Popover>
    );
};
